import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, LinearScale, CategoryScale, Tooltip } from 'chart.js';
import './RegistrationBars.css';

// Only what a bar chart with tooltips needs; anything left unregistered renders blank
ChartJS.register(BarElement, LinearScale, CategoryScale, Tooltip);

const numberFormat = new Intl.NumberFormat('en-IN');

/** Every bar up to 14 days; every 3rd at 30; every 7th at 90. The last bar always. */
export const labelStep = (days) => {
  if (days >= 90) return 7;
  if (days >= 30) return 3;
  return 1;
};

// Narrowest column that can carry a two-line "Sat / 20 Sept" label without
// touching its neighbours. Below that the step widens, since labels never rotate.
const MIN_LABEL_PX = 44;

export const isLabelled = (index, count, days, chartWidth = 0) => {
  let step = labelStep(days);
  if (chartWidth > 0 && count > 0) {
    step = Math.max(step, Math.ceil(MIN_LABEL_PX / (chartWidth / count)));
  }
  if (index === count - 1) return true;
  // A stepped label right before the (always labelled) last bar would touch it
  return index % step === 0 && count - 1 - index >= Math.ceil(step / 2);
};

// Colour with an alpha, from "#rrggbb"
const withAlpha = (hex, alpha) => {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const Skeleton = () => (
  <div className="regbars-skeleton" aria-hidden="true">
    {[42, 68, 30, 80, 55, 24, 64, 38, 72, 48, 20, 58, 36, 66].map((h, i) => (
      <span key={i} style={{ height: `${h}%` }} />
    ))}
  </div>
);

/**
 * One "accounts created per day" card. The page owns the period and the data;
 * this only draws what the API sent and never does date arithmetic itself.
 */
const RegistrationBars = ({ title, series, days, color, isDarkMode = false, loading = false, error = '', onRetry }) => {
  const count = series?.dates?.length || 0;
  const peakIndex = series ? series.dates.indexOf(series.peak?.date) : -1;
  const allZero = Boolean(series) && (series.sum || 0) === 0;

  const chartData = useMemo(() => {
    if (!series) return null;
    const fill = withAlpha(color, isDarkMode ? 0.75 : 0.7);
    return {
      labels: series.labels,
      datasets: [
        {
          data: series.total,
          backgroundColor: series.total.map((_, i) => (i === peakIndex ? color : fill)),
          hoverBackgroundColor: color,
          borderRadius: 6,
          maxBarThickness: 28,
        },
      ],
    };
  }, [series, color, isDarkMode, peakIndex]);

  const options = useMemo(() => {
    const tick = isDarkMode ? '#cbd5e1' : '#64748b';
    const grid = isDarkMode ? 'rgba(148, 163, 184, 0.25)' : 'rgba(148, 163, 184, 0.35)';
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 250 },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          backgroundColor: isDarkMode ? '#0f172a' : '#1e293b',
          titleColor: '#f8fafc',
          bodyColor: '#e2e8f0',
          padding: 10,
          callbacks: {
            title: (items) => {
              const i = items[0]?.dataIndex ?? 0;
              return `${series.weekdays[i]} ${series.labels[i]}`;
            },
            label: (item) => {
              const i = item.dataIndex;
              const made = series.total[i];
              const gone = series.deleted[i];
              return gone > 0
                ? `${numberFormat.format(made)} new (${numberFormat.format(gone)} deleted since)`
                : `${numberFormat.format(made)} new`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { color: grid },
          ticks: {
            color: tick,
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            font: { size: 10 },
            padding: 4,
            // Two lines per labelled bar: weekday, then date. `this` is the
            // axis, whose chart width decides whether every bar can be labelled.
            callback(value, index) {
              const width = this?.chart?.width || 0;
              return isLabelled(index, count, days, width) ? [series.weekdays[index], series.labels[index]] : '';
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: grid, borderDash: [4, 4] },
          border: { display: false, dash: [4, 4] },
          ticks: {
            color: tick,
            precision: 0,
            font: { size: 11 },
            callback: (value) => numberFormat.format(value),
          },
        },
      },
    };
  }, [series, count, days, isDarkMode]);

  const peakLabel = series && peakIndex >= 0
    ? `${series.weekdays[peakIndex]} ${series.labels[peakIndex]}`
    : '';

  return (
    <div className={`panel regbars ${isDarkMode ? 'regbars-dark' : ''}`} style={{ '--regbars-accent': color }}>
      <div className="panel-head">
        <div>
          <h2>{title}</h2>
          {series && !loading && !error && (
            <p>
              Last {days} days · {numberFormat.format(series.sum)} total
              {series.deleted_sum > 0 && (
                <span
                  className="regbars-muted"
                  title="Registered in this period, account deleted since"
                >
                  {' '}· {numberFormat.format(series.deleted_sum)} deleted since
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {loading ? (
        <Skeleton />
      ) : error ? (
        <div className="regbars-error" role="alert">
          <p>{error}</p>
          {onRetry && (
            <button type="button" className="btn btn-secondary" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      ) : !series ? null : allZero ? (
        <p className="panel-empty">No registrations in this period.</p>
      ) : (
        <>
          <div
            className="regbars-chart"
            role="img"
            aria-label={`${title}: ${numberFormat.format(series.sum)} in the last ${days} days, peak ${numberFormat.format(series.peak.value)} per day`}
          >
            <Bar data={chartData} options={options} />
          </div>
          <div className="regbars-foot">
            peak {numberFormat.format(series.peak.value)}/day · {peakLabel}
          </div>
        </>
      )}
    </div>
  );
};

export default RegistrationBars;
